import { Upload, Row, Space, Button, Form, Typography, Popconfirm } from 'antd'
import { CIMSModalWrapperProps } from 'components/CIMSModal/types'
import InboxOutlined from '@ant-design/icons/lib/icons/InboxOutlined'
import { useEffect, useState } from 'react'
import CIMSFormItem from 'components/CIMSFormItem'
import { HelpMessage } from 'utils/constants'
import { notEqual, pathify } from 'shared'
import { useGlobalState } from 'context/GlobalStateContext'
import {
  simpleEditNotification,
  simpleSuccessNotification,
} from 'hooks/notificationHandler'
import { rest } from 'utils/axiosInstance'
import { useProjectSettingsContext } from '../../../../ProjectSettingsContext'
import { RcFile, UploadFile } from 'antd/lib/upload/interface'
import { DefaultMutationHookOptions } from 'utils/defaults'
import FormlessModal from 'components/CIMSModal/FormlessModal'
import { useForm } from 'antd/lib/form/Form'
import CIMSSelect from 'components/CIMSSelect'
import { DeleteTwoTone } from '@ant-design/icons'
import { errorHandler } from 'hooks/errorHandler'
import { useKeyedBooleans } from 'hooks/useKeyedBooleans'
import { EditSettingsModalValues } from './types'
import { useGetInitialValues } from './hooks/useGetInitialValues'
import {
  useCreateProjectToCimsOnlineAdminMutation,
  useRemoveProjectToCimsOnlineAdminMutation,
} from 'schema'
import { useCheckPermission } from 'hooks/useCheckPermission'

const EditSettingsModal: React.FC<
  CIMSModalWrapperProps & { logo?: string }
> = ({ visible, logo, onClose }) => {
  const {
    state: { projectGlobalId },
  } = useGlobalState()
  const {
    metadata,
    refetch,
    editMetadata,
    projectLayerOptions,
    projectUserOptions,
  } = useProjectSettingsContext()
  const [loading, { add, remove }, keys] = useKeyedBooleans<
    'removing' | 'uploading' | 'editing' | 'admins'
  >()
  const [form] = useForm<EditSettingsModalValues>()
  const { Dragger } = Upload
  const [customFileList, setCustomFileList] = useState<UploadFile[]>()
  const [tempFile, setTempFile] = useState<RcFile | null>(null)
  const [clearingLayerId, setClearingLayerId] = useState<boolean>(false)
  const [clearingLogo, setClearingLogo] = useState<boolean>(false)
  const [showConfirm, setShowConfirm] = useState<boolean>(false)
  const action = pathify('api', 'projects', projectGlobalId, 'files')
  const defaultFile: UploadFile = {
    name: 'Current Logo',
    size: 123,
    type: 'custom default',
    status: 'done',
    uid: '1',
    thumbUrl: logo,
  }
  const permittedFileExtensions = ['jpeg', 'png', 'jpg']
  const permittedFileTypes = ['image/jpeg', 'image/jpg', 'image/png']
  const initialValues = useGetInitialValues(metadata)
  useEffect(() => {
    form.setFieldsValue(initialValues())
  }, [metadata, form, initialValues])

  const canMutateP2CA = useCheckPermission('admin')
  const [createP2CA] = useCreateProjectToCimsOnlineAdminMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      createProjectToCIMSOnlineAdmin: {
        admin: {
          user: { username },
        },
      },
    }) =>
      simpleSuccessNotification(
        `${username} successfully added to Project Admins`
      ),
  })
  const [removeP2CA] = useRemoveProjectToCimsOnlineAdminMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      removeProjectToCIMSOnlineAdmin: {
        admin: {
          user: { username },
        },
      },
    }) =>
      simpleSuccessNotification(
        `${username} successfully removed from Project Admins`
      ),
  })

  useEffect(() => {
    setClearingLogo(!!logo && !tempFile && customFileList?.length === 0)
  }, [logo, tempFile, customFileList])

  async function removeLogo() {
    if (!projectGlobalId) {
      throw new Error('Project id must be defined')
    }

    add('removing')
    await editMetadata({
      variables: {
        editProjectMetadataInput: {
          projectId: projectGlobalId,
          deleteProjectLogo: true,
        },
      },
      ...DefaultMutationHookOptions,
      onCompleted: () => simpleSuccessNotification('Project Logo removed'),
    })
    await refetch()
    remove('removing')
  }

  async function uploadLogo() {
    if (!tempFile) {
      return
    }

    const file = tempFile
    const formData = new FormData()
    formData.append('file', file)
    formData.append('type', 'logo')

    try {
      const splitArr = file.name.split('.')
      const extension = splitArr[splitArr.length - 1]

      if (
        !!permittedFileTypes.length &&
        !permittedFileTypes.includes(file.type.toLowerCase())
      )
        throw new Error('Invalid file type.')
      if (
        !!permittedFileExtensions.length &&
        !permittedFileExtensions.includes(extension.toLowerCase())
      )
        throw new Error('Invalid file extension.')

      add('uploading')
      await rest.post(action, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      await refetch()
      remove('uploading')
      simpleSuccessNotification('Project logo uploaded')
      // Convert image file to base64 data string
      const fileB64 = await new Promise((resolve) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result as string)
      })
      // Hard-set file list with mock data for aesthetic/rendering purposes
      setCustomFileList([
        {
          name: 'Current Logo',
          size: 123,
          type: 'custom default',
          status: 'done',
          uid: '1',
          thumbUrl: fileB64 as string,
        },
      ])
    } catch (err) {
      remove('uploading')
      errorHandler(err)
    }
  }

  return (
    <FormlessModal
      title="Edit Settings"
      visible={visible}
      cancel={{ fn: () => onClose() }}
      ok={false}
      afterClose={() => {
        setClearingLayerId(false)
        form.resetFields()
      }}
      footer={
        <Space>
          <Button
            onClick={() => {
              setCustomFileList(logo ? [defaultFile] : [])
              setTempFile(null)
              onClose()
            }}
          >
            Cancel
          </Button>
          <Popconfirm
            title={() =>
              HelpMessage.Administration.ProjectSettings.Settings.EditModalPopConfirm(
                { clearingLayerId, clearingLogo }
              )
            }
            visible={showConfirm}
            onConfirm={async () => {
              form.submit()
              setShowConfirm(false)
              setClearingLayerId(false)
            }}
            onCancel={() => setShowConfirm(false)}
          >
            <Button
              loading={!!keys.length}
              onClick={async () => {
                if (clearingLayerId || clearingLogo) setShowConfirm(true)
                else form.submit()
              }}
              type="primary"
            >
              Ok
            </Button>
          </Popconfirm>
        </Space>
      }
    >
      <Space size={'middle'} direction="vertical" style={{ width: '100%' }}>
        <Row justify="center">
          <Typography.Text>Upload Project Logo</Typography.Text>
        </Row>
        <Dragger
          listType="picture"
          style={{ padding: '10px 30px' }}
          showUploadList={{
            removeIcon: <DeleteTwoTone />,
          }}
          onPreview={() => null}
          fileList={customFileList}
          defaultFileList={logo ? [defaultFile] : []}
          accept={[
            ...permittedFileExtensions.map((val) => `.${val}`),
            ...permittedFileExtensions,
          ].join(',')}
          beforeUpload={(file) => {
            setTempFile(file)
            return false
          }}
          onChange={({ fileList }) => {
            if (fileList.length === 1) {
              setCustomFileList([fileList[0]])
            } else if (fileList.length > 1) {
              setCustomFileList([fileList[1]])
            }
          }}
          onRemove={() => {
            setCustomFileList([])
            setTempFile(null)
          }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Click or drag file to this area to upload
          </p>
          <p className="ant-upload-hint">
            Only one project logo may be uploaded at a time.
          </p>
        </Dragger>
        <Form
          form={form}
          initialValues={initialValues()}
          onFinish={async ({ cmProjectLayersId, projectAdmins }) => {
            let changesMade = false
            if (!!tempFile) {
              await uploadLogo()
              changesMade = true
            } else if (clearingLogo) {
              await removeLogo()
              changesMade = true
            }
            if (
              projectAdmins &&
              notEqual(
                projectAdmins.map(({ value }) => value.toString()),
                metadata.admins.map(({ user }) => user.id)
              )
            ) {
              /**
               * assume we're always demoting admins
               */
              const upserts = metadata.admins.map(({ user }) => ({
                userId: user.id,
                admin: false,
              }))
              /**
               * if we find an admin, remove from upserts
               *
               * otherwise upsert as admin
               */
              projectAdmins.forEach(({ value }) => {
                const foundIndex = upserts.findIndex(
                  ({ userId }) => userId === value
                )
                if (foundIndex === -1)
                  upserts.push({ userId: value.toString(), admin: true })
                else upserts.splice(foundIndex, 1)
              })
              add('admins')
              await Promise.all(
                upserts.map(({ admin, userId }) => {
                  const variables = {
                    input: { projectId: projectGlobalId!, userId },
                  }
                  if (admin) return createP2CA({ variables })
                  return removeP2CA({ variables })
                })
              )
              remove('admins')
              refetch()
              form.resetFields()
              changesMade = true
            }
            if (cmProjectLayersId !== metadata?.cmProjectLayersId) {
              if (!projectGlobalId) {
                return errorHandler('Project id must be defined')
              }

              add('editing')
              await editMetadata({
                variables: {
                  editProjectMetadataInput: {
                    projectId: projectGlobalId,
                    cmProjectLayersId: cmProjectLayersId ?? null,
                  },
                },
                onCompleted: () =>
                  simpleEditNotification('CM Project Layer ID'),
              })
              refetch()
              form.resetFields()
              remove('editing')
              changesMade = true
            }
            if (changesMade) onClose()
          }}
        >
          {canMutateP2CA && (
            <CIMSFormItem
              label="Project Admins"
              name="projectAdmins"
              requiredMessage="Please select at least 1 Project Admin"
              help={HelpMessage.Administration.ProjectAdmins}
            >
              <CIMSSelect
                options={projectUserOptions}
                multiple
                loading={loading('editing')}
              />
            </CIMSFormItem>
          )}
          <CIMSFormItem
            name="cmProjectLayersId"
            label={'CM Project Layer'}
            help={
              HelpMessage.Administration.ProjectSettings.Settings.CMProjectLayer
            }
            description={
              <>
                {clearingLayerId && (
                  <Typography.Text type="danger">
                    WARNING: If cleared, PDF Design Layers and Reports may no
                    longer work as expected
                  </Typography.Text>
                )}
                <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
                  Options are pulled from a Horrocks ArcGIS&nbsp;
                  <a
                    href="https://cims.horrocks.com/server/rest/services/CM_Project_Layers/MapServer"
                    target="_blank"
                    rel="noreferrer"
                  >
                    map server
                  </a>
                  . Reach out to the primary CIMS contact if you feel an option
                  is missing or incorrect.
                </Typography.Paragraph>
              </>
            }
          >
            <CIMSSelect
              simple
              loading={loading('editing')}
              options={projectLayerOptions}
              onChange={(value) =>
                setClearingLayerId(
                  typeof metadata?.cmProjectLayersId === 'number' &&
                    value === undefined
                )
              }
            />
          </CIMSFormItem>
        </Form>
      </Space>
    </FormlessModal>
  )
}

export default EditSettingsModal
